(function () {
  const EDGE_PADDING = 20;
  const MIN_WIDTH = 100;

  /** @type {MediaQueryList} */
  const mediaQueryLarge = matchMedia('(min-width: 750px)');

  /** @typedef {'left'|'right'|'centered'} Placement */
  /** @typedef {'top'|'bottom'|'left'|'right'} MobilePlacement */

  /** @returns {boolean} */
  function isMobileBreakpoint() {
    return !mediaQueryLarge.matches;
  }

  /**
   * @param {Element} trigger
   * @returns {HTMLElement|null}
   */
  function getTooltipFor(trigger) {
    const wrapper = trigger.closest('.tooltip-wrapper');
    return wrapper ? wrapper.querySelector('.tooltip') : null;
  }

  /**
   * @param {HTMLElement} tooltip
   * @param {MobilePlacement} placement
   * @returns {void}
   */
  function setPlacementClass(tooltip, placement) {
    tooltip.classList.remove('tooltip--top', 'tooltip--bottom', 'tooltip--left', 'tooltip--right');
    tooltip.classList.add('tooltip--' + placement);
  }

  /**
   * data-tooltip-position holds the author's original `position` param and
   * is never mutated, so it's always available to revert to once we leave
   * the mobile breakpoint — the live class can't be used for that since
   * this function is what overwrites it in the first place.
   * @param {HTMLElement} tooltip
   * @returns {void}
   */
  function applyMobilePlacement(tooltip) {
    if (tooltip.getAttribute('data-mobile-flip') !== 'true') return;
    const originalPosition = /** @type {MobilePlacement} */ (tooltip.getAttribute('data-tooltip-position')) || 'right';
    setPlacementClass(tooltip, isMobileBreakpoint() ? 'bottom' : originalPosition);
  }

  /** @returns {void} */
  function applyMobilePlacementToAll() {
    document.querySelectorAll('.tooltip[data-mobile-flip="true"]').forEach(function (tooltip) {
      applyMobilePlacement(/** @type {HTMLElement} */ (tooltip));
    });
  }

  /**
   * @param {DOMRect} tooltipRect
   * @param {DOMRect} boundaryRect
   * @returns {Placement}
   */
  function getPlacement(tooltipRect, boundaryRect) {
    if (tooltipRect.left < boundaryRect.left) return 'left';
    if (tooltipRect.right > boundaryRect.right) return 'right';
    return 'centered';
  }

  /**
   * @param {Element} trigger
   * @returns {void}
   */
  function shrinkToFit(trigger) {
    const tooltip = getTooltipFor(trigger);
    if (!tooltip) return;

    const boundaryEl = trigger.closest('[data-tooltip-boundary]');
    if (!boundaryEl) return; // no boundary to respect — leave default sizing alone

    // Reset first so we always measure the tooltip's natural width, not a
    // previously-shrunk value from an earlier hover/focus.
    tooltip.style.removeProperty('--tooltip-max-width');

    const boundaryRect = boundaryEl.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const placement = getPlacement(tooltipRect, boundaryRect);

    const boundaryLeft = boundaryRect.left;
    const boundaryRight = boundaryRect.right;

    let availableWidth = tooltipRect.width;

    if (placement === 'centered') {
      const overflowRight = Math.max(0, tooltipRect.right - boundaryRight);
      const overflowLeft = Math.max(0, boundaryLeft - tooltipRect.left);
      const overflow = Math.max(overflowRight, overflowLeft);
      availableWidth = tooltipRect.width - (2 * overflow);
    } else if (placement === 'right') {
      const overflow = Math.abs(tooltipRect.right - boundaryRight);
      availableWidth = tooltipRect.width - overflow - EDGE_PADDING;
    } else if (placement === 'left') {
      const overflow = Math.abs(boundaryLeft - tooltipRect.left);
      availableWidth = tooltipRect.width - overflow - EDGE_PADDING;
    }

    if (availableWidth < tooltipRect.width) {
      availableWidth = Math.max(MIN_WIDTH, Math.floor(availableWidth));
      tooltip.style.setProperty('--tooltip-max-width', availableWidth + 'px');
    }
  }

  /**
   * @param {Element} trigger
   * @returns {void}
   */
  function handleTriggerActivate(trigger) {
    const tooltip = getTooltipFor(trigger);
    if (tooltip) applyMobilePlacement(tooltip);
    shrinkToFit(trigger);
  }

  document.addEventListener('click', function (e) {
    const trigger = e.target && /** @type {Element} */ (e.target).closest && /** @type {Element} */ (e.target).closest('.tooltip-trigger');
    if (trigger) handleTriggerActivate(trigger);
  });

  document.addEventListener('mouseover', function (e) {
    const trigger = e.target && /** @type {Element} */ (e.target).closest && /** @type {Element} */ (e.target).closest('.tooltip-trigger');
    if (trigger) handleTriggerActivate(trigger);
  });

  // 'focus' does not bubble, so a listener on document never fires for it.
  // 'focusin' bubbles and is what actually catches keyboard/tab focus.
  document.addEventListener('focusin', function (e) {
    const trigger = e.target && /** @type {Element} */ (e.target).closest && /** @type {Element} */ (e.target).closest('.tooltip-trigger');
    if (trigger) handleTriggerActivate(trigger);
  });

  // Covers the case where the viewport crosses the breakpoint while a
  // tooltip happens to already be open (e.g. rotating a device, resizing
  // a browser window) — everything else is applied lazily on hover/focus.
  applyMobilePlacementToAll();
  mediaQueryLarge.addEventListener('change', applyMobilePlacementToAll);
})();